import { Loader, Stack, VisuallyHidden } from "@mantine/core";
import { useState } from "react";

import { ErrorAlert } from "../../platform/ErrorAlert";
import { useStreamStatus } from "../../platform/stream-status";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { MediaGrid } from "../../ui/MediaGrid";
import { MediaPlaceholder } from "../../ui/MediaCard";
import type { Medium } from "../../platform/studio/draft";
import type { DraftShape } from "../../platform/studio/draft-state";
import { GenerationDetailModal } from "../../platform/jobs/detail/GenerationDetailModal";
import { GenerationPreview, previewOutputs } from "./GenerationPreview";
import { GenerationRow } from "./GenerationRow";
import { GENERATIONS_PAGE, useJobList } from "../../platform/jobs/job-list";
import { groupGenerations } from "./generations";
import { jobMedium } from "../../platform/jobs/detail/outputs";

export interface MyGenerationsProps {
  teamId: string;
  brandId: string;
  medium: Medium;
  /** The composer's count and ratio: with no job yet, the cards show at them. */
  shape: DraftShape;
}

/**
 * The brand's jobs of this page's medium, newest first, as rows (G4's list, kept live by the
 * stream), a talking head's actors in one row as live's (issue 238). With none yet, soft boxes at the composer's count and ratio show where they will
 * appear, as the live app does (design-system.md 3.3).
 */
export function MyGenerations({ teamId, brandId, medium, shape }: MyGenerationsProps) {
  // The page's own medium alone, live's 5 at a time (`medium`, issue 784).
  const jobs = useJobList(
    teamId,
    { status: "all", brandId, medium, pageSize: GENERATIONS_PAGE },
    useStreamStatus(),
  );
  // The job whose detail is open over the list (design-system.md 3.4), or none.
  const [open, setOpen] = useState<string | null>(null);
  // The output the lightbox shows (Daniil's request, G1 decision 28), or none.
  const [previewing, setPreviewing] = useState<string | null>(null);
  const listed = jobs.data?.pages.flatMap((page) => page.items) ?? [];
  const items = groupGenerations(listed.filter((job) => jobMedium(job) === medium));
  const more = morePages(jobs);
  if (jobs.isPending) return <Loader size="sm" aria-label="Loading your generations" />;
  if (jobs.isError) return <ErrorAlert error={jobs.error} />;
  if (items.length === 0 && !jobs.hasNextPage) {
    return (
      <Stack gap="sm" pt={24}>
        <MediaGrid label={`Your next ${medium === "video" ? "videos" : "images"}`}>
          {Array.from({ length: shape.outputs }, (_, at) => (
            <MediaPlaceholder key={at} aspectRatio={shape.aspectRatio} label={`New ${medium}`} medium={medium} />
          ))}
        </MediaGrid>
        {/* Live shows the cards alone; a screen reader still hears what they are. */}
        <VisuallyHidden>
          Your {medium === "video" ? "videos" : "images"} appear here as you make them.
        </VisuallyHidden>
      </Stack>
    );
  }
  return (
    <Stack gap={0} pt={24} aria-label="My Generations">
      {items.map((generation) => (
        <GenerationRow
          key={generation.key}
          teamId={teamId}
          brandId={brandId}
          jobs={generation.jobs}
          readAgain={() => void jobs.refetch()}
          onOpen={setOpen}
          onPreview={setPreviewing}
        />
      ))}
      {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
      <LoadAhead more={more} loaded={listed.length} label="Loading more generations" />
      {open === null ? null : (
        <GenerationDetailModal teamId={teamId} jobId={open} onClose={() => setOpen(null)} />
      )}
      <GenerationPreview
        teamId={teamId}
        brandId={brandId}
        outputs={previewOutputs(items)}
        current={previewing}
        onShow={setPreviewing}
        more={more}
        readAgain={() => void jobs.refetch()}
      />
    </Stack>
  );
}
